/**
 * PageMeta - Centralized SEO meta tags for all routes
 * Uses React 19 native metadata support + hreflang for international SEO
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = "MarkeeMix";
const BASE_URL = "https://markeemix.com";

// Route → Meta configuration
const routeMeta = {
  "/": {
    title: `${SITE_NAME} - AI Workspace for Teachers, Creators & Marketers | 75+ Tools`,
    description: "MarkeeMix is an AI workspace for teachers, creators, and marketers. Live SerpAPI keyword research, AutoCaptions, PPT generator, PDF suite, YouTube CRM, and 70+ more AI tools — one workspace, one price.",
  },
  "/tools": {
    title: `All Tools - ${SITE_NAME} | 75+ AI Tools for Teachers, Creators & Marketers`,
    description: "Browse every tool inside MarkeeMix — SEO, content, YouTube, PDF, design, teaching, ads. Free & Pro tiers. No credit card.",
  },
  "/pricing": {
    title: `Pricing - ${SITE_NAME} | Free · Starter ₹199 · Pro ₹499 · Business ₹1499`,
    description: "Simple India-first pricing. Start free, pay only when you scale. Starter plan ₹199/mo for solo teachers and creators. Pro & Business plans include unlimited AI generations.",
  },
  "/login": {
    title: `Log in - ${SITE_NAME}`,
    description: "Sign in to your MarkeeMix workspace.",
  },
  "/signup": {
    title: `Sign up free - ${SITE_NAME}`,
    description: "Create your MarkeeMix account in 30 seconds. Free forever tier — no credit card required.",
  },
  "/dashboard": {
    title: `Dashboard - ${SITE_NAME}`,
    description: "Your MarkeeMix workspace overview: recent tools, creator earnings, quick actions.",
  },
  "/workspace": {
    title: `Workspace - ${SITE_NAME} | Project workflows for teachers, creators & marketers`,
    description: "Project-oriented workspace — pick a goal, MarkeeMix runs the right tools in the right order.",
  },
  "/ai-studio": {
    title: `AI Studio - ${SITE_NAME} | Claude & GPT-powered marketing tools`,
    description: "Live SerpAPI keyword research, SEO content grader, meta tag generator, topical maps, SERP simulator — powered by Claude Sonnet.",
  },
  "/teaching": {
    title: `Teaching Suite - ${SITE_NAME} | AI lesson plans, worksheets, quizzes for teachers`,
    description: "Every classroom workflow — automated. Lesson plans, worksheets, rubrics, homework, parent communication, course outlines. Built for Indian K-12 teachers.",
  },
  "/youtube-suite": {
    title: `YouTube Suite - ${SITE_NAME} | Channel assessments, scripts, SEO`,
    description: "Grow any YouTube channel with real-data assessments, AI script writing, thumbnail studio, and end-to-end SEO.",
  },
  "/content-suite": {
    title: `Content Suite - ${SITE_NAME} | Blog, social, email content`,
    description: "AI-powered blog writer, social post generator, email sequences, and content calendar — all in one place.",
  },
  "/creator/incentive": {
    title: `Creator Earnings - ${SITE_NAME} | Get paid ₹50/short · ₹150/video`,
    description: "Publish on YouTube, paste the link, get paid. Simple creator incentive program with month-wise tracking and WhatsApp reminders.",
  },
  "/free/keyword-research": {
    title: `Free Keyword Research - ${SITE_NAME}`,
    description: "Free keyword research tool — real SerpAPI data. No login required for 3 daily searches.",
  },
  "/free/word-counter": {
    title: `Free Word Counter - ${SITE_NAME}`,
    description: "Counts words, characters, sentences, reading time. Instant results, zero signup.",
  },
  "/tools/auto-captions": {
    title: `AutoCaptions - ${SITE_NAME} | AI captions for video (Hindi + Hinglish supported)`,
    description: "Upload a video and get accurate, styled captions in 30+ languages including Hinglish (Roman-script Hindi). Export SRT + branded MP4.",
  },
  "/tools/ai-slides": {
    title: `AI PPT Generator - ${SITE_NAME} | 10 premium themes + custom colors`,
    description: "Turn a topic into a polished slide deck in seconds. Pick from 10 curated themes or use your brand colors.",
  },
  "/tools/design/pdf": {
    title: `PDF Editor - ${SITE_NAME} | Merge · Split · Compress · Sign · Edit`,
    description: "Complete PDF toolkit — merge, split, compress, add text, highlight, stamp, protect, unlock, and AI-summarize any PDF.",
  },
  "/tools/design/thumbnail-studio": {
    title: `AI Thumbnail Studio - ${SITE_NAME} | Live canvas + YouTube reference thumbnails`,
    description: "Analyze any topic or URL, get 6 AI text ideas and top-10 real YouTube reference thumbnails, then edit on a 1280×720 canvas.",
  },
  "/tools/google-ads": {
    title: `Google Ads Copy Generator - ${SITE_NAME} | Claude Sonnet 4.5 powered`,
    description: "Business-context aware ad copy generator — 15 headlines, 4 descriptions, callouts, sitelinks. Optimized for Google Ads character limits.",
  },
  "/about": {
    title: `About - ${SITE_NAME} | Our mission: honest AI tools for the rest of us`,
    description: "MarkeeMix is a founding-stage AI workspace built by a small team for teachers, creators, and marketers who don't want a 15-app stack.",
  },
  "/contact": {
    title: `Contact - ${SITE_NAME}`,
    description: "Get in touch with the MarkeeMix team. We reply within 24 hours.",
  },
  "/blog": {
    title: `Blog - ${SITE_NAME} | AI, SEO & marketing insights`,
    description: "Practical guides on AI-powered marketing, SEO, YouTube growth, and content strategy.",
  },
  "/privacy": {
    title: `Privacy Policy - ${SITE_NAME}`,
    description: "How MarkeeMix handles your data. Short version: we keep only what's needed to run the product, never sell it, and let you export or delete everything.",
  },
  "/terms": {
    title: `Terms of Service - ${SITE_NAME}`,
    description: "MarkeeMix Terms of Service.",
  },
};

// Dynamic route patterns
const dynamicRouteMeta = [
  {
    pattern: /^\/report\/(.+)$/,
    getMeta: () => ({
      title: `SEO Report - ${SITE_NAME} | Detailed Performance Analysis`,
      description: "View your shared SEO performance report with detailed metrics, keyword rankings, and actionable insights."
    })
  },
  {
    pattern: /^\/free\/(.+)$/,
    getMeta: (pathname) => {
      const tool = pathname.split('/').pop().replace(/-/g, ' ');
      return {
        title: `Free ${tool.charAt(0).toUpperCase() + tool.slice(1)} - ${SITE_NAME}`,
        description: `Use our free ${tool} tool. No signup required. Get instant results with MarkeeMix.`
      };
    }
  },
  {
    pattern: /^\/tools\/(.+)$/,
    getMeta: (pathname) => {
      const tool = pathname.split('/').pop().replace(/-/g, ' ');
      return {
        title: `${tool.charAt(0).toUpperCase() + tool.slice(1)} Tool - ${SITE_NAME}`,
        description: `Professional ${tool} tool by MarkeeMix. Boost your digital marketing with AI-powered insights.`
      };
    }
  },
];

function getMetaForPath(pathname) {
  if (routeMeta[pathname]) return routeMeta[pathname];
  for (const route of dynamicRouteMeta) {
    if (route.pattern.test(pathname)) return route.getMeta(pathname);
  }
  return {
    title: `${SITE_NAME} - AI Workspace for Teachers, Creators & Marketers`,
    description: "MarkeeMix offers 75+ AI tools built for teachers, creators, and marketers for SEO, content marketing, social media, and advertising. Start free today."
  };
}

export default function PageMeta() {
  const location = useLocation();
  const meta = getMetaForPath(location.pathname);
  const url = `${BASE_URL}${location.pathname}`;

  const titleStr = meta.title || `${SITE_NAME} - AI Workspace for Teachers, Creators & Marketers`;
  const descStr = meta.description || '';
  const keywordsStr = meta.keywords || 'SEO tools, digital marketing, content marketing, MarkeeMix';

  useEffect(() => {
    document.title = titleStr;

    const metaTags = {
      'description': descStr,
      'keywords': keywordsStr,
      'og:type': 'website',
      'og:url': url,
      'og:title': titleStr,
      'og:description': descStr,
      'og:site_name': SITE_NAME,
      'og:locale': 'en_US',
      'twitter:card': 'summary_large_image',
      'twitter:url': url,
      'twitter:title': titleStr,
      'twitter:description': descStr,
      'robots': 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    };

    Object.entries(metaTags).forEach(([key, value]) => {
      const isOg = key.startsWith('og:') || key.startsWith('twitter:');
      const attr = isOg ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    });

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Hreflang for international SEO
    const hreflangTags = [
      { lang: 'en', href: url },
      { lang: 'x-default', href: url },
    ];
    // Remove old hreflang
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    hreflangTags.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    });
  }, [location.pathname, titleStr, descStr, keywordsStr, url]);

  return null;
}
